import React, { useState, useEffect } from "react";
import Axios from "utils/Axios";
import MKButton from "components/MKButton";
import Icon from "@mui/material/Icon";
import { useNavigate } from "react-router-dom";
import coverImageDefault from "../../../assets/images/groove-sign-up.jpg";
import Avatar from "@mui/material/Avatar";
import truncate from "truncate-html";
import decodeToken from "utils/DecodeToken";
import MKInput from "components/MKInput";
import Pagination from "@mui/material/Pagination";

function Events() {
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(9);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userInfo = decodeToken();

  if (userInfo.access < 2) navigate(`/`);

  const imageUrl = (url) => {
    return process.env.REACT_APP_BASE_IMAGE_URL + url;
  };

  const toggleModal = () => {
    navigate("/promoter/events/new");
  };

  const gotoEventDetails = (eventId) => {
    navigate(`/promoter/events/single/${eventId}`);
  };

  const truncateHtml = (htmlContent, length = 100) => {
    return truncate(htmlContent, {
      length: length,
      ellipsis: "...",
      keepImageTag: true,
    });
  };

  const fetchEvents = (pageNum = 1, query = "") => {
    setLoading(true);
    Axios.get(`?type=event&token=${token}&search=${query}&page=${pageNum}&limit=${limit}`)
      .then((response) => {
        const eventData = response.data.data || [];
        const flattenedEvents = eventData.flatMap((dateGroup) =>
          dateGroup.events?.map((event) => ({
            id: event.id,
            title: event.title,
            description: event.description,
            time: event.time,
            image: event.image,
            location: event.location,
            ticketPrice: event.ticketPrice,
            date: event.eventDate,
            status: event.is_active,
          }))
        );
        setEvents(flattenedEvents);
        
        // Set pagination data from the API response
        if (response.data.pagination) {
          setTotalItems(response.data.pagination.total);
          setTotalPages(response.data.pagination.totalPages);
          setPage(response.data.pagination.page);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch events data", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchEvents(1);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPage(1); // Reset to first page on search change
    if (value.length >= 2) {
      fetchEvents(1, value);
    } else if (value.length === 0) {
      fetchEvents(1);
    }
  };
  
  // Handle page change for pagination
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchEvents(newPage, search);
  };

  return (
    <div className="promoterEvents">
      <div className="AddEvent flex justify-between max-sm:flex-col">
        <div className="max-sm:flex max-sm:flex-row max-sm:justify-between max-sm:items-center">
          <div className="lg:mx-12 my-2">
            <div className="text-2xl font-bold py-0 text-primary">Events</div>
            <div className="text-sm font-light py-2">List of your events:</div>
          </div>
          <div className="max-sm:mb-2 max-sm:block hidden">
          <MKButton onClick={toggleModal}>
            <Icon color="inherit">add_circle</Icon>
            &nbsp; Add Event
          </MKButton>
          </div>
        </div>
        <div className="addEventText flex gap-x-4 max-sm:flex-col">
          <div>
            <div className="searchBar w-64 bg-white rounded-lg">
              <MKInput
                color="white"
                value={search}
                onChange={handleSearchChange}
                fullWidth
                size="small"
                placeholder="Search events..."
              />
            </div>
          </div>
          <div className="max-sm:hidden">
          <MKButton onClick={toggleModal}>
            <Icon color="inherit">add_circle</Icon>
            &nbsp; Add Event
          </MKButton>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <p>Loading events...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <p>No events found.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6 p-4">
          {events?.map((event) => (
            <div
              onClick={() => gotoEventDetails(event.id)}
              key={event.id}
              className="bg-white border border-gray-200 text-gray-800 p-4 rounded-lg shadow-lg relative cursor-pointer"
            >
              <div className="flex gap-x-8">
                <Avatar
                  alt={event.title}
                  src={imageUrl(event.image) || coverImageDefault}
                  sx={{ width: 56, height: 56 }}
                  variant="rounded"
                />
                <div className="">
                  <h2 className="text-lg font-bold">{event.title}</h2>
                  <p className="text-xs font-thin text-green-600">{event.location}</p>
                  {event.status === "0" ? (
                    <div>
                      <p className="text-xs font-thin text-white bg-yellow-600 max-w-fit px-2 py-0.5 rounded-md">
                        Pending
                      </p>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </div>
              <div className="mt-10">
                <p
                  className="text-sm text-gray-600"
                  dangerouslySetInnerHTML={{
                    __html: truncateHtml(event.description, 100),
                  }}
                ></p>
              </div>
              <div className="mt-5">
                <p className="text-xs text-gray-600 mt-2">
                  Event Date: {event.date + " " + event.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {totalItems > 0 && (
        <div className="flex flex-col items-center mt-6 mb-4">
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange} 
            color="primary" 
            size="large"
          />
          <div className="text-center text-sm mt-2">
            Showing page {page} of {totalPages} ({totalItems} total events)
          </div>
        </div>
      )}
    </div>
  );
}

export default Events;
